import type { ReactNode } from "react"
import type { SearchResultType } from "@/types/v2/search"

// Icon set for the command palette (CON-183). The shared
// `@/lib/inbox-icons` covers the magnifier + chevrons we
// reuse for chrome; these are the result-type glyphs the
// palette needs that don't exist elsewhere (deal diamond,
// company building, note bookmark) plus a clock glyph for
// the recent-searches rows.
//
// All glyphs default to the muted v2 stroke (#7A746B) and
// take a `size` so the type-icon swatch can scale them.

const MUTED = "#7A746B"

interface GlyphProps {
  size?: number
  stroke?: string
  strokeWidth?: number
}

function Svg({ size, children }: { size: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

// Deal — diamond, matching the deals nav glyph's silhouette.
export function DealGlyph({
  size = 16,
  stroke = MUTED,
  strokeWidth = 1.4,
}: GlyphProps) {
  return (
    <Svg size={size}>
      <path
        d="M8 2L13.5 8L8 14L2.5 8L8 2Z"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </Svg>
  )
}

// Company — building, matching the companies nav glyph.
export function CompanyGlyph({
  size = 16,
  stroke = MUTED,
  strokeWidth = 1.3,
}: GlyphProps) {
  return (
    <Svg size={size}>
      <path
        d="M3 13.5V3.2C3 2.9 3.22 2.7 3.5 2.7H8.5C8.78 2.7 9 2.9 9 3.2V13.5M9 13.5V7.2C9 6.9 9.22 6.7 9.5 6.7H12.5C12.78 6.7 13 6.9 13 7.2V13.5M2.5 13.5H13.5M5 5.2H7M5 8H7M5 10.8H7M11 9.4H11.5M11 11.5H11.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

// Note — bookmark.
export function NoteGlyph({
  size = 16,
  stroke = MUTED,
  strokeWidth = 1.4,
}: GlyphProps) {
  return (
    <Svg size={size}>
      <path
        d="M4 2.75H12C12.28 2.75 12.5 2.97 12.5 3.25V13.5L8 10.75L3.5 13.5V3.25C3.5 2.97 3.72 2.75 4 2.75Z"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </Svg>
  )
}

// Recent-search glyph — a clock to read as "searched
// before". Distinct from the magnifier so the cold-start
// "RECENT SEARCHES" rows don't look like fresh results.
export function HistoryGlyph({
  size = 16,
  stroke = MUTED,
  strokeWidth = 1.4,
}: GlyphProps) {
  return (
    <Svg size={size}>
      <circle cx="8" cy="8" r="5.5" stroke={stroke} strokeWidth={strokeWidth} />
      <path
        d="M8 4.75V8L10.25 9.25"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  )
}

// Map a result type to its glyph. Contact + conversation
// render an initials avatar instead (handled by the row),
// so this only covers the glyph kinds — callers fall back to
// the avatar for those two.
export function ResultTypeGlyph({
  type,
  size = 15,
}: {
  type: SearchResultType
  size?: number
}) {
  switch (type) {
    case "deal":
      return <DealGlyph size={size} />
    case "company":
      return <CompanyGlyph size={size} />
    case "note":
      return <NoteGlyph size={size} />
    default:
      // contact / conversation never reach here — the row
      // renders an avatar. Render nothing as a safe fallback.
      return null
  }
}

// Whether a result type uses an initials avatar (people-ish:
// contacts and conversations) vs. a glyph swatch.
export function usesAvatar(type: SearchResultType): boolean {
  return type === "contact" || type === "conversation"
}
